import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./ui/kit";
import api from "../lib/api";
import { ROLE_LABEL } from "../lib/nav";
import { sekceMenu, jeAktivni } from "../lib/mobilniNav";

/**
 * Menu modulů přes celou obrazovku (plátno 16A3) — na telefonu a tabletu
 * místo levého panelu. Nahoře hledání modulu a křížek (tam, kde bylo ☰),
 * pak profil, bílé karty po sekcích a odhlášení úplně dole.
 */
export default function MobilMenu({ onClose, odznaky }) {
  const { user, company, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [hledat, setHledat] = useState("");
  const [clenstvi, setClenstvi] = useState([]);
  const [prepinam, setPrepinam] = useState(false);
  const vstup = useRef(null);

  useEffect(() => {
    api.get("/api/auth/clenstvi").then(({ data }) => setClenstvi(data?.items || [])).catch(() => {});
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    const puvodni = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", esc); document.body.style.overflow = puvodni; };
  }, [onClose]);

  const sekce = useMemo(() => {
    const q = hledat.trim().toLowerCase();
    return sekceMenu(user)
      .map((s) => ({ ...s, polozky: q ? s.polozky.filter((p) => p.label.toLowerCase().includes(q)) : s.polozky }))
      .filter((s) => s.polozky.length);
  }, [user, hledat]);

  const jdi = (path) => { onClose(); nav(path); };
  const odhlasit = async () => { onClose(); await logout(); nav("/login"); };
  const prepnout = async (m) => {
    if (m.current || prepinam) return;
    setPrepinam(true);
    try { await api.post("/api/auth/prepnout", { membership_id: m.membership_id }); window.location.assign("/"); }
    catch { setPrepinam(false); }
  };
  const role = (user?.roles || []).map((r) => ROLE_LABEL[r] || r).join(", ");

  return (
    <div className="lg:hidden fixed inset-0 z-50 bg-surface flex flex-col menu-in" data-testid="mobil-menu" role="dialog" aria-modal="true" aria-label="Menu">
      <div className="px-3.5 pt-[calc(env(safe-area-inset-top)+8px)] pb-2 flex items-center gap-2.5">
        <label className="flex-1 min-w-0 h-11 rounded-full bg-white shadow-[0_0_0_1px_#eeece7] flex items-center gap-2.5 px-4 text-sec">
          <Icons.Search size={18} strokeWidth={2.2} className="shrink-0" />
          <input ref={vstup} value={hledat} onChange={(e) => setHledat(e.target.value)} placeholder="Hledat modul"
            className="flex-1 min-w-0 bg-transparent outline-none text-[16px] text-ink placeholder:text-sec" data-testid="mobil-menu-hledat" />
        </label>
        <button type="button" onClick={onClose} aria-label="Zavřít menu" data-testid="sidebar-close"
          className="tap h-11 w-11 rounded-full bg-white shadow-[0_0_0_1px_#eeece7] text-ink inline-flex items-center justify-center shrink-0">
          <Icons.X size={18} strokeWidth={2.2} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3.5 pb-[calc(env(safe-area-inset-bottom)+24px)]">
        {!hledat && (
          <>
            <button type="button" onClick={() => jdi("/nastaveni")} data-testid="mobil-menu-profil"
              className="w-full bg-white rounded-[20px] p-3.5 flex items-center gap-3 text-left">
              <Avatar name={user?.name} url={user?.photo_url} size={52} className="!rounded-full" />
              <span className="min-w-0 flex-1">
                <span className="block text-[17px] font-700 text-ink truncate">{user?.name}</span>
                <span className="block t-meta text-muted truncate">{[company?.name, role].filter(Boolean).join(" · ")}</span>
              </span>
              <Icons.ChevronRight size={18} className="text-muted shrink-0" />
            </button>
            {clenstvi.length > 1 && (
              <div className="mt-2 bg-white rounded-[20px] overflow-hidden" data-testid="account-switcher">
                {clenstvi.map((m, i) => (
                  <button key={m.membership_id} type="button" onClick={() => prepnout(m)} disabled={m.current || prepinam}
                    data-testid={`account-${m.company_id}`}
                    className={`w-full h-[54px] px-3.5 flex items-center gap-3 text-left ${i ? "border-t border-line-soft" : ""}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block t-telo font-600 text-ink truncate">{m.company_name}</span>
                      <span className="block t-meta text-muted truncate">{(m.roles || []).map((r) => ROLE_LABEL[r] || r).join(", ")}</span>
                    </span>
                    {m.current ? <span className="h-2 w-2 rounded-full bg-accent shrink-0" /> : <Icons.ArrowRightLeft size={15} className="text-muted shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {sekce.map((s) => (
          <div key={s.klic}>
            {s.titul ? <div className="label-up text-muted px-1.5 pt-4 pb-[7px]">{s.titul}</div> : <div className="h-2" />}
            <div className="bg-white rounded-[20px] overflow-hidden">
              {s.polozky.map((p, j) => {
                const Icon = Icons[p.icon] || Icons.Circle;
                const on = jeAktivni(p, pathname);
                const cervene = odznaky.odznak(p.key);
                const pocet = !cervene && p.countKey ? odznaky.souhrn[p.countKey] : null;
                return (
                  <button key={p.key} type="button" onClick={() => jdi(p.path)} data-testid={`nav-${p.key}`} aria-current={on ? "page" : undefined}
                    className={`w-full h-[54px] px-3.5 flex items-center gap-3 text-left active:bg-inputbg ${j ? "border-t border-line-soft" : ""}`}>
                    <span className={`h-[34px] w-[34px] rounded-[11px] inline-flex items-center justify-center shrink-0 ${on ? "bg-accent text-white" : "bg-surface text-sec"}`}>
                      <Icon size={18} strokeWidth={1.9} />
                    </span>
                    <span className={`flex-1 min-w-0 truncate text-[16px] text-ink ${on ? "font-700" : "font-600"}`}>{p.label}</span>
                    {cervene > 0 && (
                      <span className="amount min-w-[22px] h-[22px] px-1.5 rounded-full bg-danger text-white text-[11.5px] font-700 inline-flex items-center justify-center">{cervene}</span>
                    )}
                    {pocet != null && <span className="amount t-meta font-600 text-muted">{pocet}</span>}
                    <Icons.ChevronRight size={16} strokeWidth={2} className="text-[#c4c2bd] shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {hledat && !sekce.length && <p className="px-2 pt-6 t-telo text-muted text-center">Žádný modul se nejmenuje „{hledat}“.</p>}

        <button type="button" onClick={odhlasit} data-testid="logout-button"
          className="tap mt-3 px-2 py-3 flex items-center gap-2.5 t-telo font-600 text-danger">
          <Icons.LogOut size={18} strokeWidth={2} /> Odhlásit se
        </button>
      </div>
    </div>
  );
}
